import {
  DebtSaleDetailsError,
  DebtSaleDetailsResult,
  DebtSaleDetailsResultTypenames,
  DebtSaleDetailsSuccess,
} from '@varo/graphql-schema-types';
import FetchDebtSaleDetails from 'services/graphql/queries/FetchDebtSaleDetails';
import { CombinedError, useQuery } from 'urql';

export enum DebtSaleDetailsStatus {
  Success = 'Success',
  NoDebt = 'NoDebt',
  Error = 'Error',
}

export type DebtSaleDetailsSuccessSelection = Omit<
  DebtSaleDetailsSuccess,
  'title'
> & {
  pageTitle: DebtSaleDetailsSuccess['title'];
};

export type DebtSaleDetailsState =
  | {
      status: DebtSaleDetailsStatus.Success;
      success: DebtSaleDetailsSuccessSelection;
    }
  | { status: DebtSaleDetailsStatus.NoDebt }
  | { status: DebtSaleDetailsStatus.Error; typedError: DebtSaleDetailsError };

export interface UseDebtSaleDetailsResult {
  result: DebtSaleDetailsState | null;
  fetching: boolean;
  error: CombinedError | DebtSaleDetailsError | null;
  refetch: () => void;
}

type DebtSaleDetailsQueryResult =
  | Exclude<DebtSaleDetailsResult, DebtSaleDetailsSuccess>
  | DebtSaleDetailsSuccessSelection;

const toDebtSaleDetailsState = (
  debtSaleDetails: DebtSaleDetailsQueryResult | undefined,
): DebtSaleDetailsState | null => {
  if (
    debtSaleDetails?.__typename ===
    DebtSaleDetailsResultTypenames.DebtSaleDetailsSuccess
  ) {
    return { status: DebtSaleDetailsStatus.Success, success: debtSaleDetails };
  }

  if (
    debtSaleDetails?.__typename ===
    DebtSaleDetailsResultTypenames.DebtSaleDetailsNoDebt
  ) {
    return { status: DebtSaleDetailsStatus.NoDebt };
  }

  if (
    debtSaleDetails?.__typename ===
    DebtSaleDetailsResultTypenames.DebtSaleDetailsError
  ) {
    return { status: DebtSaleDetailsStatus.Error, typedError: debtSaleDetails };
  }

  return null;
};

const useDebtSaleDetails = (): UseDebtSaleDetailsResult => {
  const [{ data, fetching, error }, refetch] = useQuery<{
    debtSaleDetails: DebtSaleDetailsQueryResult;
  }>({
    query: FetchDebtSaleDetails,
    requestPolicy: 'cache-and-network',
  });

  const result = toDebtSaleDetailsState(data?.debtSaleDetails);

  const typedError =
    result?.status === DebtSaleDetailsStatus.Error ? result.typedError : null;

  return {
    result,
    fetching: fetching && !data,
    error: error ?? typedError,
    refetch,
  };
};

export default useDebtSaleDetails;
